import GeoJSON from "ol/format/GeoJSON";
import { equalTo } from "ol/format/filter";
import WFS from "ol/format/WFS";
import TrailwiseEventBus from "./TrailwiseEventBus";
import { UsrnSearchResult, UsrnSearchSource } from "./UsrnSearchContracts";
import { getCenter } from "ol/extent";

export class UsrnSearchService {

    private readonly _geoJson = new GeoJSON();
    private readonly _wfsFormat = new WFS();
    private readonly _wfsVersion = "2.0.0";
    private readonly _propertyNames: string[];

    public constructor(
        private wfsUrl: string,
        private layerName: string = "OpenUSRN",
        propertyNames?: string[]
    ) {
        this._propertyNames = propertyNames && propertyNames.length > 0
            ? propertyNames
            : ["USRN", "usrn"];        
    }

    public search(query: string, source: UsrnSearchSource): Promise<UsrnSearchResult> {
        const trimmedQuery = (query || "").trim();
        if (!trimmedQuery) {
            return Promise.reject("empty");
        }

        return this.trySearchWithFields(trimmedQuery, source, 0);
    }

    private trySearchWithFields(query: string, source: UsrnSearchSource, index: number): Promise<UsrnSearchResult> {
        if (index >= this._propertyNames.length) {
            TrailwiseEventBus.usrnSearchFailure({ query, source });
            return Promise.reject("notfound");
        }

        const property = this._propertyNames[index];
        const numericQuery = Number(query);
        const isNumeric = !isNaN(numericQuery);

        const filterValue = isNumeric ? numericQuery : query;
        const filterString = this.buildFilterString(property, filterValue);
        const requestUrl = this.buildRequestUrl(filterString);

        return new Promise<UsrnSearchResult>((resolve, reject) => {
            $.ajax({
                url: requestUrl,
                method: "GET",
                dataType: "json"
            })
                .done((response) => {
                    const features = this._geoJson.readFeatures(response, {
                        dataProjection: "EPSG:3857",
                        featureProjection: "EPSG:3857"
                    });

                    const match = this.findMatchingFeature(features, query, property);
                    if (!match) {
                        this.trySearchWithFields(query, source, index + 1)
                            .then(resolve)
                            .catch(reject);
                        return;
                    }

                    const geometry = match.getGeometry();
                    const extent = geometry.getExtent();
                    const coordinate = geometry.getClosestPoint(getCenter(extent));
                    const result: UsrnSearchResult = {
                        feature: match,
                        coordinate,
                        extent,
                        layerName: this.layerName
                    };
                    TrailwiseEventBus.usrnSearchSuccess({ query, result, source });
                    resolve(result);
                })
                .fail(() => {
                    this.trySearchWithFields(query, source, index + 1)
                        .then(resolve)
                        .catch(reject);
                });
        });
    }

    private buildFilterString = (property: string, value: string | number): string => {
        const filter = equalTo(property, value);
        const featureRequest: Node = this._wfsFormat.writeGetFeature({
            srsName: "EPSG:3857",
            featureNS: "",
            featurePrefix: "",
            featureTypes: [this.layerName],
            filter
        });

        const documentNode = featureRequest as Document;
        const filters = documentNode.getElementsByTagName("Filter");
        const filterNode = filters.length > 0 ? filters[0] : null;
        if (!filterNode) {
            return "";
        }
        let filterStr = new XMLSerializer().serializeToString(filterNode);
        if (filterStr.indexOf("<ogc:") === -1) {
            filterStr = filterStr
                .replace(/<Filter/g, '<ogc:Filter')
                .replace(/<PropertyIsEqualTo/g, '<ogc:PropertyIsEqualTo')
                .replace(/<PropertyName/g, '<ogc:PropertyName')
                .replace(/<Literal/g, '<ogc:Literal')
                .replace(/<\/Literal/g, '</ogc:Literal')
                .replace(/<\/PropertyName/g, '</ogc:PropertyName')
                .replace(/<\/PropertyIsEqualTo/g, '</ogc:PropertyIsEqualTo')
                .replace(/<\/Filter/g, '</ogc:Filter');

            if (filterStr.indexOf("xmlns:ogc") === -1) {
                filterStr = filterStr.replace(
                    '<ogc:Filter',
                    '<ogc:Filter xmlns:ogc="http://www.opengis.net/ogc"'
                );
            }
        }
        filterStr = filterStr.replace(/ xmlns="http:\/\/www\.opengis\.net\/ogc"/g, "");
        return filterStr;
    }

    private buildRequestUrl = (filterStr: string): string => {
        const separator = this.wfsUrl.indexOf("?") > -1 ? "&" : "?";
        const params = [
            "SERVICE=WFS",
            `VERSION=${this._wfsVersion}`,
            "REQUEST=GetFeature",
            `TYPENAMES=${this.layerName}`,
            "SRSNAME=EPSG:3857",
            "OUTPUTFORMAT=geojson",
            `FILTER=${encodeURIComponent(filterStr)}`
        ];

        return `${this.wfsUrl}${separator}${params.join("&")}`;
    }

    private findMatchingFeature = (features: any[], query: string, property: string) => {
        const normalizedQuery = query.trim().toLowerCase();
        const numericQuery = Number(query);
        const isNumeric = !isNaN(numericQuery);
        return features.find(feature => {
            const value = feature.get(property) ?? feature.get(property.toUpperCase()) ?? feature.get(property.toLowerCase());
            if (value === undefined || value === null) {
                return false;
            }
            if (isNumeric) {
                const numericValue = Number(value);
                if (isNaN(numericValue)) {
                    return false;
                }
                return numericValue === numericQuery;
            }
            return value.toString().trim().toLowerCase() === normalizedQuery;
        });
    }
}
